/**
 * Org settings mapping for the organization form: what the form reads from the
 * org payload and what it sends back as subscription_information.
 */

/**
 * Reads org settings: MFA from the org root only; trial/chat from
 * subscription_information (with legacy top-level fallbacks for feature flags).
 */
export function getOrgSubscriptionSettingsFromApi(org) {
  const sub = org?.subscription_information;
  return {
    mfa_required_for_org: Boolean(org?.mfa_required_for_org),
    trial_experience_enabled: Boolean(
      sub?.has_trial_experience ?? org?.trial_experience_enabled,
    ),
    chat_diligent_enabled: Boolean(
      sub?.has_chat_diligent ?? org?.chat_diligent_enabled,
    ),
    has_auto_score: sub?.has_auto_score ?? true,
    auto_score_quota: sub?.auto_score_quota ?? null,
    auto_score_unlimited: sub?.auto_score_unlimited ?? true,
    auto_score_used: sub?.auto_score_used ?? 0,
    has_sme_score: sub?.has_sme_score ?? false,
    sme_score_quota: sub?.sme_score_quota ?? null,
    sme_score_unlimited: sub?.sme_score_unlimited ?? false,
    sme_score_used: sub?.sme_score_used ?? 0,
  };
}

/**
 * Builds subscription_information for PATCH: spreads existing API fields, then
 * updates risk entitlements (always) plus optional trial/chat flags (sponsor only).
 * MFA is sent on the payload root, not here.
 *
 * includeFeatureFlags gates the sponsor-only trial/chat flags; vendors never
 * receive those. Risk entitlements are sent for both org types, the provider
 * columns stay in place even though vendors no longer see risk scoring.
 */
export function buildSubscriptionInformationPatch(org, orgData, options = {}) {
  const { includeFeatureFlags = true } = options;
  const existing =
    org?.subscription_information &&
    typeof org.subscription_information === "object"
      ? { ...org.subscription_information }
      : {};
  const riskEntitlements = {
    has_auto_score: !!orgData.has_auto_score,
    auto_score_quota: orgData.auto_score_quota,
    auto_score_unlimited: !!orgData.auto_score_unlimited,
    has_sme_score: !!orgData.has_sme_score,
    sme_score_quota: orgData.sme_score_quota,
    sme_score_unlimited: !!orgData.sme_score_unlimited,
  };
  if (!includeFeatureFlags) {
    return { ...existing, ...riskEntitlements };
  }
  return {
    ...existing,
    ...riskEntitlements,
    has_trial_experience: !!orgData.trial_experience_enabled,
    has_chat_diligent: !!orgData.chat_diligent_enabled,
  };
}
